// src/utils/request.ts
import { history } from '@umijs/max';
import { message } from 'antd';
import { getValidToken, clearToken, subscribeToTokenRefresh } from './tokenManager';

const baseURL = process.env.API_BASE_URL || '';
const REQUEST_TIMEOUT = 10000; // 10 seconds

const errorMessages: Record<number, string> = {
  400: 'Bad Request - Invalid request parameters',
  401: 'Session expired. Please login again.',
  403: 'Access Denied - Insufficient permissions',
  404: 'Resource Not Found',
  408: 'Request Timeout - Please try again',
  500: 'Internal Server Error - Please contact support',
  502: 'Bad Gateway - Service temporarily unavailable',
  503: 'Service Unavailable - Please try again later',
};

// Token management is now handled by tokenManager.ts

async function errorHandler(error: any) {
  const { response } = error;

  if (response instanceof Response) {
    let errorText = errorMessages[response.status] || response.statusText || 'Unknown Error';

    try {
      const json = await response.clone().json();
      errorText = json?.message || json?.error || errorText;
    } catch (parseError) {
      // If JSON parsing fails, try to get text content
      try {
        const text = await response.clone().text();
        if (text && text.length < 200) {
          errorText = text;
        }
      } catch {
        // Use default error message if all parsing fails
      }
    }

    // Don't show error message for 401 as it's handled separately
    if (response.status !== 401) {
      message.error(errorText);
    }
    
    return Promise.reject(new Error(errorText));
  }

  // Handle network errors
  if (error?.name === 'TypeError' && error?.message?.includes('fetch')) {
    const networkError = 'Network Error: Cannot connect to server. Please check your internet connection.';
    message.error(networkError);
    return Promise.reject(new Error(networkError));
  }

  const errorMessage = error?.message || 'An unexpected error occurred. Please try again.';
  message.error(errorMessage);
  return Promise.reject(error);
}

function buildQueryString(params: Record<string, any>) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

async function timeoutFetch(url: string, options: RequestInit, timeout: number): Promise<Response> {
  const timeoutPromise = new Promise<Response>((_resolve, reject) => {
    setTimeout(() => reject(new Error('Request timeout')), timeout);
  });

  return Promise.race([fetch(url, options), timeoutPromise]) as Promise<Response>;
}

async function retryRequest(url: string, options: RequestInit, token: string) {
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function request<T = any>(url: string, options: any = {}): Promise<T> {
  // Get valid token (will refresh if needed)
  let token: string;
  try {
    token = await getValidToken();
  } catch (error) {
    console.error('Failed to get valid token:', error);
    clearToken();
    message.warning('Authentication failed. Redirecting to login page...');
    setTimeout(() => {
      const currentPath = window.location.pathname;
      if (currentPath !== '/user/login') {
        history.push('/user/login');
      }
    }, 1000);
    return Promise.reject(new Error('Authentication failed'));
  }

  const authHeader = { Authorization: `Bearer ${token}` };

  const { params, ...otherOptions } = options;

  let finalUrl = `${baseURL}${url}`;
  if (params && typeof params === 'object' && !(params instanceof Array)) {
    const queryString = buildQueryString(params);
    if (queryString) {
      finalUrl += (finalUrl.includes('?') ? '&' : '?') + queryString;
    }
  }

  const fetchOptions: RequestInit = {
    ...otherOptions,
    headers: {
      ...(otherOptions.headers || {}),
      ...authHeader,
    },
    credentials: 'same-origin',
  };

  if (
    fetchOptions.method &&
    ['POST', 'PUT', 'PATCH'].includes(fetchOptions.method.toUpperCase()) &&
    options.data &&
    typeof options.data === 'object' &&
    !(options.data instanceof FormData)
  ) {
    fetchOptions.body = JSON.stringify(options.data);
    fetchOptions.headers = {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers || {}),
    };
  } else if (options.data instanceof FormData) {
    fetchOptions.body = options.data;
  }

  let response: Response;
  try {
    response = await timeoutFetch(finalUrl, fetchOptions, REQUEST_TIMEOUT);
  } catch (error) {
    return errorHandler(error);
  }

  // Handle 401 Unauthorized - This should be rare now since we refresh proactively
  if (response.status === 401) {
    console.warn('401 Unauthorized detected - token may have been revoked');
    clearToken();
    message.error('Session expired. Please login again.');
    setTimeout(() => {
      const currentPath = window.location.pathname;
      if (currentPath !== '/user/login') {
        history.push('/user/login');
      }
    }, 1000);
    return Promise.reject(new Error('Session expired'));
  }

  if (!response.ok) {
    await errorHandler({ response });
    return Promise.reject(new Error(`HTTP error: ${response.status}`));
  }

  // const data = await response.json();
  let data: any;
  let acceptHeader = '';

  if (fetchOptions.headers && typeof fetchOptions.headers === 'object' && !Array.isArray(fetchOptions.headers)) {
    const headersObj = fetchOptions.headers as Record<string, string>;
    acceptHeader = headersObj['Accept'] || '';
  }

  const responseType = options.responseType ?? 'json';

  if (responseType === 'blob') {
    data = await response.blob();
  } else if (responseType === 'text') {
    data = await response.text();
  } else {
    data = await response.json();
  }

  return data;

}
